import { constants, mediaQueryStyles } from "@newsela/angelou";

import {
  bodyFontSizeMapping,
  bodyLineHeight,
  headingFontSizeMapping,
  headingLineHeightMapping,
} from "@/view/constants/styles/accessibleFontSizeMapping/accessibleFontSizeMapping.style";
import type { SizeOptionKey } from "@/view/SecondaryActions/FontSizeAction/sizeOptions";
import type { CSSObject } from "@emotion/react";

const { breakpoints, colors } = constants;
const { mediaQuery } = mediaQueryStyles;
const { xs } = breakpoints.BreakpointNames;

export const rootCss: CSSObject = {
  color: colors.ui.neutral[600],
  marginBottom: "20px",
  maxWidth: "720px",
  position: "relative",

  [mediaQuery.upTo(xs)]: {
    maxWidth: "100vw",
    padding: "0 16px",
  },
};

const paragraphCountBadgeIndent = "40px";
const maxParagraphWidth = "720px";

const getMaxParagraphWidth = (showParagraphCount: boolean) => {
  if (showParagraphCount) {
    return `calc(${maxParagraphWidth} - ${paragraphCountBadgeIndent})`;
  }
  return maxParagraphWidth;
};

export const bodyCss = (showParagraphCount: boolean): CSSObject => ({
  paddingLeft: showParagraphCount ? paragraphCountBadgeIndent : "0",
});

export const countBadgeMarginMapping = {
  small: "5px",
  medium: "10px",
  large: "15px",
  extraLarge: "20px",
};

export const tiptapCss = (
  currentFontSize: SizeOptionKey,
  showParagraphCount: boolean,
): CSSObject => {
  const pFontSize = bodyFontSizeMapping[currentFontSize];
  const h2FontSize = headingFontSizeMapping(true)[
    currentFontSize
  ] as CSSObject["fontSize"];
  const h3FontSize = headingFontSizeMapping()[
    currentFontSize
  ] as CSSObject["fontSize"];

  if (showParagraphCount) {
    return {
      "&>div:first-of-type": {
        "&>div:first-of-type": {
          counterReset: "paragraph-count",
          p: {
            fontSize: pFontSize,
            lineHeight: bodyLineHeight,
            marginBottom: "30px",
            marginBlockStart: 0,
          },
          "p[data-identifier]::before": {
            counterIncrement: "paragraph-count",
            content: "counter(paragraph-count)",
            color: "white",
            position: "absolute",
            left: -40,
            marginTop: countBadgeMarginMapping[currentFontSize],
            backgroundColor: colors.ui.core[500],
            borderRadius: "50%",
            minWidth: "15px",
            minHeight: "15px",
            width: "auto",
            height: "auto",
            textAlign: "center",
            padding: "5px",
            lineHeight: "100%",
            fontSize: "0.875rem",
          },
          h2: {
            color: colors.ui.neutral[700],
            fontSize: h2FontSize,
            lineHeight: headingLineHeightMapping(true)[currentFontSize],
          },
          h3: {
            color: colors.ui.neutral[700],
            fontSize: h3FontSize,
            lineHeight: headingLineHeightMapping()[currentFontSize],
          },
        },
      },
    };
  }
  return {
    "&>div:first-of-type": {
      "&>div:first-of-type": {
        p: {
          fontSize: bodyFontSizeMapping[currentFontSize],
          lineHeight: bodyLineHeight,
          marginBottom: "30px",
          marginBlockStart: 0,
        },
        h2: {
          color: colors.ui.neutral[700],
          fontSize: h2FontSize,
          lineHeight: headingLineHeightMapping(true)[currentFontSize],
        },
        h3: {
          color: colors.ui.neutral[700],
          fontSize: h3FontSize,
          lineHeight: headingLineHeightMapping()[currentFontSize],
        },
      },
    },
  };
};

export const nodeCss = (showParagraphCount: boolean): CSSObject => ({
  // Targeting the paragraph ProseMirrorNode
  // see ProseMirrorNode.js __cssFor propTypes for more details
  paragraph: {
    maxWidth: getMaxParagraphWidth(showParagraphCount),
  },
});
